import { useEffect, useRef, useState } from "react";
import { Trash2, UserPlus, Pencil, Check } from "lucide-react";
import { ContactLinks } from "@/components/contacts/ContactLinks";
import { InviteContact, InviteStatus } from "@/components/contacts/InviteContact";
import { PrivateNote } from "@/components/contacts/PrivateNote";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const blank = { name: "", details: "", notes: "", phone: "", whatsapp: "", email: "", instagram: "", linkedin: "", tiktok: "", profession: "", city: "" };
const FIELDS = ["phone", "whatsapp", "email", "instagram", "linkedin", "tiktok"];

export const ContactsDialog = ({ w, b, index, children, editAt = null }) => {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [contacts, setContacts] = useState(w.contacts);
  const [notes, setNotes] = useState(w.notes);
  const [form, setForm] = useState(blank);

  const queue = useRef(Promise.resolve());
  const filled = (f) => [f.name, f.details, ...FIELDS.map((k) => f[k])].some((v) => v.trim());
  const clean = (f) => {
    const c = Object.fromEntries(Object.keys(blank).map((k) => [k, (f[k] || "").trim()]));
    // Keep link/invite bookkeeping the form doesn't show
    const kept = Object.fromEntries(["external_id", "invite_code", "invited_at", "joined_user_id", "pid", "friend_id"].filter((k) => f[k]).map((k) => [k, f[k]]));
    return { ...c, ...kept, name: c.name || c.details || c.email || c.phone || c.instagram || c.whatsapp || c.tiktok || c.linkedin };
  };
  const merged = (list) => (editing === null ? [...list, clean(form)] : list.map((c, j) => (j === editing ? clean(form) : c)));

  // Contacts are saved the moment they're added or removed; saves run in order
  const persist = (list, n, msg) => {
    queue.current = queue.current.then(() => b.patchWish(w.id, { contacts: list, notes: n }, msg));
    return queue.current;
  };
  const withPending = () => (filled(form) ? merged(contacts) : contacts);
  const dirty = () => filled(form) || notes !== w.notes;

  // Keep the open dialog in sync when an Undo restores the saved list
  useEffect(() => { if (open) setContacts(w.contacts); }, [w.contacts]); // eslint-disable-line react-hooks/exhaustive-deps

  const onOpen = (o) => {
    if (o) {
      setContacts(w.contacts); setNotes(w.notes);
      setEditing(editAt); setForm(editAt === null ? blank : { ...blank, ...w.contacts[editAt] });
    }
    else if (dirty()) persist(withPending(), notes, "Trip details saved");
    setOpen(o);
  };
  const add = (e) => {
    e.preventDefault();
    if (!filled(form)) return;
    const next = merged(contacts);
    setContacts(next);
    setForm(blank);
    setEditing(null);
    persist(next, notes, editing === null ? `${clean(form).name} saved as a contact` : `${clean(form).name} updated`);
  };
  const remove = (i) => {
    const next = contacts.filter((_, j) => j !== i);
    setContacts(next);
    if (editing !== null) { setEditing(null); setForm(blank); }
    persist(next, notes, "Contact removed");
  };
  const save = async () => {
    if (dirty()) await persist(withPending(), notes, "Trip details saved");
    setForm(blank);
    setEditing(null);
    setOpen(false);
  };
  const edit = (i) => { setEditing(i); setForm({ ...blank, ...contacts[i] }); };
  const field = (k) => ({ value: form[k], onChange: (e) => setForm({ ...form, [k]: e.target.value }) });

  return (
    <Dialog open={open} onOpenChange={onOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid={`contacts-dialog-${index}`}
        onPointerDownOutside={(e) => { if (e.target.closest?.("[data-sonner-toaster]")) e.preventDefault(); }}
        onInteractOutside={(e) => { if (e.target.closest?.("[data-sonner-toaster]")) e.preventDefault(); }}>
        <DialogHeader>
          <p className="eyebrow !text-amber-400/80">#{String(index).padStart(2, "0")} on the list</p>
          <DialogTitle className="font-display text-2xl">{w.country}</DialogTitle>
          <DialogDescription>People who can make the trip smoother, plus your planning notes.</DialogDescription>
        </DialogHeader>
        <ul className="space-y-2 max-h-64 overflow-y-auto">
          {contacts.length === 0 && <li className="text-sm text-slate-500">No contacts yet.</li>}
          {contacts.map((c, i) => (
            <li key={i} className="flex items-start justify-between gap-3 rounded-lg border border-white/10 p-3" data-testid={`contact-item-${i}`}>
              <div className="min-w-0 flex-1">
                <p className="font-medium">{c.name}{c.profession && <span className="text-xs text-sky-300/80 font-normal"> · {c.profession}</span>}{c.city && <span className="text-xs text-amber-200/80 font-normal"> · {c.city}</span>}</p>
                <InviteStatus c={c} testId={`contact-invite-status-${i}`} />
                {c.details && <p className="text-xs font-mono text-amber-200/80 break-all">{c.details}</p>}
                {c.notes && <p className="text-xs text-slate-400 mt-1">{c.notes}</p>}
              </div>
              <ContactLinks c={c} testId={`contact-links-${i}`} />
              <InviteContact c={c} wishId={w.id} index={i} testId={`contact-invite-${i}`} />
              <div className="flex flex-col gap-2 pt-1">
                <button onClick={() => edit(i)} className={`hover:text-amber-300 ${editing === i ? "text-amber-300" : "text-slate-500"}`} data-testid={`contact-edit-${i}`} aria-label="Edit contact"><Pencil size={14} /></button>
                <button onClick={() => remove(i)} className="text-slate-500 hover:text-red-400" data-testid={`contact-delete-${i}`} aria-label="Remove contact"><Trash2 size={14} /></button>
              </div>
            </li>
          ))}
        </ul>
        <PrivateNote />
        <form onSubmit={add} className="grid grid-cols-2 gap-2 rounded-xl bg-white/[0.03] p-3 border border-white/5">
          {editing !== null && <p className="col-span-2 text-xs text-amber-300" data-testid="contact-editing-label">Editing {contacts[editing]?.name}</p>}
          <Input placeholder="Name" {...field("name")} data-testid="contact-name-input" />
          <Input placeholder="Professions, e.g. Influencer, Celebrity" {...field("profession")} data-testid="contact-profession-input" />
          <Input placeholder="City they live in (optional)" {...field("city")} className="col-span-2" data-testid="contact-city-input" />
          <Input placeholder="Phone" type="tel" inputMode="tel" {...field("phone")} data-testid="contact-phone-input" />
          <Input placeholder="WhatsApp (if different)" type="tel" inputMode="tel" {...field("whatsapp")} data-testid="contact-whatsapp-input" />
          <Input placeholder="Email" type="email" inputMode="email" {...field("email")} data-testid="contact-email-input" />
          <Input placeholder="Instagram @handle" {...field("instagram")} autoCapitalize="none" data-testid="contact-instagram-input" />
          <Input placeholder="TikTok @handle" {...field("tiktok")} autoCapitalize="none" data-testid="contact-tiktok-input" />
          <Input placeholder="LinkedIn profile link" {...field("linkedin")} autoCapitalize="none" className="col-span-2" data-testid="contact-linkedin-input" />
          {(form.details || (editing !== null && contacts[editing]?.details)) && <Input placeholder="Other details" {...field("details")} className="col-span-2" data-testid="contact-details-input" />}
          <Input placeholder="How they can help (e.g. host in Lima)" {...field("notes")} className="col-span-2" data-testid="contact-notes-input" />
          <div className="col-span-2 flex gap-2">
            {editing !== null && <Button type="button" variant="ghost" size="sm" onClick={() => { setEditing(null); setForm(blank); }} data-testid="contact-edit-cancel">Cancel</Button>}
            <Button type="submit" variant="outline" size="sm" className="flex-1 bg-transparent" data-testid="add-contact-button">
              {editing === null ? <><UserPlus size={14} /> Add contact</> : <><Check size={14} /> Save contact</>}
            </Button>
          </div>
        </form>
        <Textarea placeholder="Trip notes…" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} data-testid="wish-notes-input" />
        <DialogFooter>
          <Button onClick={save} className="bg-amber-500 text-[#1a1203] hover:bg-amber-400" data-testid="contacts-save-button">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
